<template>
    <div
        class="relative min-h-[100px] w-full"
        :class="{ 'mb-6': hasFooter }"
    >
        <div class="relative w-full">
            <textarea
                id="textarea"
                ref="textarea"
                class="peer rounded-input focus:border-focus block min-h-[100px] w-full resize-y overflow-y-auto border px-3 pt-3 text-sm focus:ring-0 focus:outline-hidden"
                :class="{
                    'border-error': errorMessage,
                    'border-muval-gray-4 hover:border-muval-gray-3 bg-white': !errorMessage,
                    'placeholder-gray-400': !label && placeholder,
                    'bg-muval-gray-5!': disabled,
                    'placeholder-transparent': !forcePlaceholderVisible,
                    'posthog-sensitive': censor,
                }"
                :rows="rows"
                :name="vid"
                :placeholder="placeholder ? placeholder : label"
                :value="inputValue"
                :maxlength="limit ?? undefined"
                :disabled="disabled"
                :readonly="readonly"
                @change="(e) => (!updateOnInput ? handleChange(e) : null)"
                @input="(e) => (updateOnInput ? handleChange(e) : null)"
                @blur="handleBlur"
            />
            <div
                v-if="label"
                class="peer peer-focus:text-focus skeleton:hidden pointer-events-none absolute -top-2 left-1 z-1 line-clamp-1 text-xs text-gray-400 transition-all duration-75 peer-placeholder-shown:top-2 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-focus:-top-2 peer-focus:text-xs"
            >
                <span class="px-2">{{ label }}</span>
                <div class="absolute top-2 -z-10 h-0.5 w-full bg-white dark:bg-gray-900"></div>
            </div>

            <!-- Single strip so the error, the SMS warning and the counter share one
                 row. They used to be two stacked `absolute -bottom-5` layers, which
                 collided whenever an error showed alongside the "Long message" chip. -->
            <div class="absolute -bottom-5 left-0 flex w-full items-center justify-between gap-x-2 whitespace-nowrap">
                <div
                    v-if="errorMessage"
                    class="text-xxs text-error min-w-0 truncate"
                >
                    {{ errorMessage }}
                </div>
                <MuvalTippy
                    v-if="smsWarning && smsSegments > 1"
                    triggers="mouseenter"
                    :interactive="false"
                    class="shrink-0"
                >
                    <p class="text-muval-amber cursor-default text-xs font-medium">Long message</p>
                    <template #content>
                        This message will use {{ smsSegments }} SMS credits (1 per 160 characters). Merge tags may
                        affect the final count.
                    </template>
                </MuvalTippy>
                <p
                    v-if="!errorMessage && limit !== null"
                    class="my-auto ml-auto shrink-0 text-xs text-gray-400"
                >
                    {{ limitChar }} / {{ limit }}
                </p>
            </div>
            {{ textarea?.innerText }}
        </div>
        <!-- Skeleton -->
        <div class="skeleton:loader"></div>
    </div>
</template>
<script setup lang="ts">
import type { PropType } from 'vue';
import { useField } from 'vee-validate';
import { idGen } from '@utilities/idGen';

const props = defineProps({
    modelValue: {
        type: [String, null],
        required: false,
        default: undefined,
    },
    label: {
        type: [String, null],
        required: false,
        default: null,
    },
    placeholder: {
        type: [String, null],
        required: false,
        default: null,
    },
    vid: {
        type: String,
        required: false,
        default: () => idGen(6),
    },
    // null disables the maxlength and the character counter.
    limit: {
        type: Number as PropType<number | null>,
        required: false,
        default: 3000,
    },
    disabled: {
        type: Boolean,
        required: false,
        default: false,
    },
    readonly: {
        type: Boolean,
        required: false,
        default: false,
    },
    // Sync the field on every keystroke rather than on blur. Off by default:
    // `change` only fires on blur for a textarea, so the character counter sits
    // stale while typing. Opt in where a live counter matters.
    updateOnInput: {
        type: Boolean,
        required: false,
        default: false,
    },
    forcePlaceholderVisible: {
        type: Boolean,
        required: false,
        default: false,
    },
    rows: {
        type: Number,
        required: false,
        default: 1,
    },
    smsWarning: {
        type: Boolean,
        required: false,
        default: false,
    },
    censor: {
        type: Boolean,
        required: false,
        default: false,
    },
});

const vid = toRef(props, 'vid');
const {
    value: inputValue,
    errorMessage,
    handleBlur,
    handleChange,
} = useField(vid, undefined, {
    syncVModel: true,
});

const limit = toRef(props, 'limit');
const limitChar = computed(() => {
    if (
        typeof limit.value === 'number' &&
        Number.isInteger(limit.value) &&
        Number.isInteger(inputValue.value?.length)
    ) {
        return Math.min(inputValue.value.length, limit.value);
    }
    return 0;
});

const SMS_SEGMENT_LENGTH = 160;
const smsSegments = computed(() => {
    if (!props.smsWarning || !inputValue.value?.length) return 0;
    return Math.ceil(inputValue.value.length / SMS_SEGMENT_LENGTH);
});

// The bottom strip (error, SMS warning, counter) sits below the textarea, so the
// margin is only reserved while something can show there.
const hasFooter = computed(
    () => props.limit !== null || Boolean(errorMessage.value) || (props.smsWarning && smsSegments.value > 1),
);
</script>
